'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useMemo, useRef, useState } from 'react';

import { CasesSliderDto } from '@/components/service-pages/CasesSlider/types';
import AnimatedButton from '@/components/shared/AnimatedButton';
import SliderNavButtons from '@/components/shared/SliderNavButtons';
import { Typography } from '@/components/shared/Typography';
import VimeoPlayer from '@/components/shared/VimeoPlayer';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { useVimeoViewportPlayback } from '@/hooks/useVimeoViewportPlayback';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	data: CasesSliderDto;
	isIndustry?: boolean;
}

const CasesSlider: FC<Props> = ({ data, isIndustry = false }) => {
	const device = useDevice();
	const isMobile = device === 'mobile';

	const wrapperRef = useRef<HTMLElement>(null);
	const slidesCounterRef = useRef<HTMLDivElement>(null);
	const navigationRef = useRef<HTMLDivElement>(null);
	const mediaRef = useRef<HTMLDivElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const descriptionRef = useRef<HTMLParagraphElement>(null);
	const buttonRef = useRef<HTMLDivElement>(null);
	const resultsRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const articlesRefs = useRef<(HTMLElement | null)[]>([]);

	const [index, setIndex] = useState(0);
	const vimeoPlaying = useVimeoViewportPlayback(wrapperRef);

	const caseCount = data.cases?.length ?? 0;
	const hasMultipleCases = caseCount > 1;

	const animationRefs = [mediaRef, titleRef, descriptionRef, buttonRef, resultsRef, listRef];
	const visibleSlides = useMemo(
		() => (isMobile ? data.cases : [data.cases?.[index]]) || [],
		[isMobile, index, data.cases]
	);

	const animateContentOut = (tl: gsap.core.Timeline) => {
		const params = { opacity: 0, scale: 0.96 };

		animationRefs.forEach((ref, index) => {
			tl.to(ref.current, params, !index ? undefined : '<+=0.1');
		});
	};

	const animateContentIn = (tl: gsap.core.Timeline) => {
		const params = { opacity: 1, scale: 1, y: 0 };
		animationRefs.forEach((ref, index) => {
			tl.to(ref.current, params, !index ? undefined : '<+=0.1');
		});
	};

	useGSAP(
		() => {
			articlesRefs.current.forEach(article => {
				if (!article) return;
				gsap.fromTo(
					article,
					{ opacity: 0, y: 40 },
					{
						opacity: 1,
						y: 0,
						duration: 0.6,
						ease: 'power2.out',
						scrollTrigger: {
							trigger: article,
							start: 'top bottom-=100px',
							end: 'bottom top',
							toggleActions: 'play none none reverse'
						}
					}
				);
			});
		},
		{ scope: wrapperRef, dependencies: [visibleSlides] }
	);

	useGSAP(
		() => {
			if (!hasMultipleCases) return;

			const counterEl = slidesCounterRef.current;
			const navEl = navigationRef.current;
			if (!counterEl || !navEl) return;

			const navTl = gsap.timeline({
				scrollTrigger: {
					trigger: wrapperRef.current,
					start: 'top bottom-=100px',
					end: 'bottom top',
					toggleActions: 'play none none reverse'
				}
			});

			navTl.fromTo(
				[counterEl, navEl],
				{ y: 40 },
				{ y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.1 }
			);
		},
		{ scope: wrapperRef, dependencies: [hasMultipleCases] }
	);

	const changeSlide = (next: number) => {
		const tl = gsap.timeline({
			defaults: { duration: 0.5, ease: 'power2.out' }
		});

		animateContentOut(tl);
		tl.add(() => setIndex(next));
		animateContentIn(tl);
	};

	const onClickPrevious = () => {
		changeSlide((index - 1 + data.cases.length) % data.cases.length);
	};

	const onClickNext = () => {
		changeSlide((index + 1) % data.cases.length);
	};

	return (
		<section
			ref={wrapperRef}
			className={clsx(css.section, 'container', isIndustry && css.industry)}
		>
			{hasMultipleCases ? (
				<header className={css.header}>
					<Typography
						ref={slidesCounterRef}
						className={css.slidesCounter}
						variant="body-l"
						tag="div"
					>
						{index + 1} / {caseCount}
					</Typography>

					<SliderNavButtons
						ref={navigationRef}
						className={css.navigationEntrance}
						onPrevious={onClickPrevious}
						onNext={onClickNext}
					/>
				</header>
			) : null}

			{visibleSlides.map((slide, i) => {
				const isIndustry = slide.isIndustry || false;

				return (
					<article
						key={i}
						ref={el => {
							if (el) articlesRefs.current[i] = el;
						}}
						className={clsx(css.content, isIndustry && css.contentIndustry)}
					>
						<div className={css.mainText}>
							<Typography
								ref={isMobile ? null : titleRef}
								uppercase
								className={css.title}
								variant={isIndustry ? 'h2-industry' : 'h4'}
								tag="h2"
							>
								<StrapiRichContent content={slide.title} />
							</Typography>
							<Typography
								ref={isMobile ? null : descriptionRef}
								className={css.description}
								variant="body-m"
							>
								<StrapiRichContent content={slide.description} />
							</Typography>

							<div ref={isMobile ? null : buttonRef} className={css.buttonWrapper}>
								{slide.buttonUrl ? (
									<AnimatedButton
										type="link"
										className={clsx(css.caseLink, css.caseLinkDesktop, isIndustry && css.industry)}
										href={slide.buttonUrl}
									>
										{slide.buttonLabel || 'Visit case'}
									</AnimatedButton>
								) : null}

								{slide.buttonUrlOutline ? (
									<AnimatedButton
										type="link"
										href={slide.buttonUrlOutline}
										className={clsx(
											css.caseLinkOutline,
											css.caseLinkDesktop,
											isIndustry && css.industry
										)}
									>
										{slide.buttonLabelOutline || 'See more case studies'}
									</AnimatedButton>
								) : null}
							</div>
						</div>

						<div
							ref={isMobile ? null : mediaRef}
							className={clsx(css.mediaWrapper, isIndustry && css.mediaWrapperIndustry)}
						>
							{slide.videoUrl ? (
								<VimeoPlayer
									fitCover
									playing
									url={slide.videoUrl}
									thumbnailUrl={slide.videoThumbnailUrl || undefined}
									className={css.video}
								/>
							) : slide.image?.url ? (
								<Image
									className={css.image}
									width={960}
									height={530}
									src={slide.image.url}
									alt=""
								/>
							) : null}
						</div>

						<div ref={isMobile ? null : resultsRef} className={css.resultsWrapper}>
							<Typography
								uppercase={isIndustry}
								variant={isIndustry ? 'h3-industry' : 'h6'}
								tag="h3"
							>
								{slide.resultsTitle || 'Results'}
							</Typography>
							<Typography variant="body-m" tag="div">
								<StrapiRichContent content={slide.resultsText} />
							</Typography>
						</div>

						<ul ref={isMobile ? null : listRef} className={css.resultsList}>
							{slide.resultsList?.map((resultItem, index) => (
								<li key={index} className={css.resultItem}>
									{resultItem.numericTitle ? (
										<p className={css.resultItemNumericTitle}>{resultItem.numericTitle}</p>
									) : (
										<p
											className={clsx(
												css.resultItemTitle,
												isIndustry && css.resultItemTitleIndustry
											)}
										>
											{resultItem.title}
										</p>
									)}

									{resultItem.text ? (
										<div className={clsx(css.resultItemText)}>
											<StrapiRichContent content={resultItem.text} />
										</div>
									) : null}
								</li>
							))}
						</ul>

						<div className={clsx(css.mobileLinksWrapper, isIndustry && css.industry)}>
							{slide.buttonUrl ? (
								<Link className={clsx(css.caseLink, css.caseLinkMobile)} href={slide.buttonUrl}>
									<Typography
										className={clsx(css.caseLinkText, isIndustry && css.caseLinkTextIndustry)}
										variant={isIndustry ? 'body-s' : 'body-m'}
										tag="span"
									>
										{slide.buttonLabel || 'Visit case'}
									</Typography>
								</Link>
							) : null}

							{slide.buttonUrlOutline ? (
								<Link
									href={slide.buttonUrlOutline}
									className={clsx(
										css.caseLinkOutline,
										css.caseLinkMobile,
										isIndustry && css.industry
									)}
								>
									<Typography
										variant={isIndustry ? 'body-s' : 'body-m'}
										tag="span"
										className={clsx(
											css.caseLinkOutlineText,
											isIndustry && css.caseLinkOutlineTextIndustry
										)}
									>
										{slide.buttonLabelOutline || 'See more case studies'}
									</Typography>
								</Link>
							) : null}
						</div>
					</article>
				);
			})}
		</section>
	);
};

export default CasesSlider;
